import { Link } from 'react-router-dom';

import { Alert } from '../../../../components/Alert';
import { PageSpinner } from '../../../../components/PageSpinner';
import portalStyles from '../../../../components/PortalComponents.module.css';
import { StatusBadge } from '../../../../components/StatusBadge';
import { billingApi } from '../../../../features/billing/billing.api';
import { useSessionQuery as useQuery } from '../../../../hooks/useSessionQuery';
import { toDisplayMessage } from '../../../../lib/error-message';
import { formatNgn } from '../../../../lib/format-currency';
import { formatDate } from '../../../../lib/format-date';
import { paths, routeTo } from '../../../../routes/paths';
import styles from '../../AppPages.module.css';

export function PaymentHistoryPage(): React.JSX.Element {
  const payments = useQuery({
    queryKey: ['customer', 'payments'],
    queryFn: () => billingApi.listPayments({ pageSize: 50 }),
  });
  const attempts = useQuery({
    queryKey: ['customer', 'payment-attempts'],
    queryFn: () => billingApi.listPaymentAttempts({ pageSize: 50 }),
  });

  const items = payments.data?.items ?? [];
  const attemptItems = attempts.data?.items ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Payments</h1>
          <p className={styles.pageSubtitle}>
            Completed and posted instalment payments on your account.
          </p>
        </div>
        <Link to={paths.customerRepayments}>View repayments</Link>
      </header>

      <section className={styles.card} aria-labelledby="posted-payments-heading">
        <h2 className={styles.cardTitle} id="posted-payments-heading">
          Posted payments
        </h2>
        <p className={styles.pageSubtitle}>
          Only confirmed transactions that were posted to your repayment account appear here.
        </p>

        {payments.isPending && <PageSpinner label="Loading posted payments" />}
        {payments.isError && <Alert tone="error">{toDisplayMessage(payments.error)}</Alert>}

        {payments.isSuccess && items.length === 0 && (
          <p className={styles.empty}>No posted payments recorded yet.</p>
        )}

        {payments.isSuccess && items.length > 0 && (
          <div className={portalStyles.compactTableWrapper}>
            <table className={portalStyles.compactTable} aria-label="Posted payments">
              <thead>
                <tr>
                  <th scope="col">Posted</th>
                  <th scope="col">Reference</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Method</th>
                  <th scope="col">Status</th>
                  <th scope="col">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {items.map((payment) => (
                  <tr key={payment.id}>
                    <td>{formatDate(payment.postedAt)}</td>
                    <td>{payment.internalReference}</td>
                    <td>{formatNgn(payment.amount)}</td>
                    <td>{payment.paymentMethod}</td>
                    <td>
                      <StatusBadge status={payment.status} />
                    </td>
                    <td>
                      <Link to={routeTo.customerPaymentReceipt(payment.id)}>View receipt</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={styles.card} aria-labelledby="payment-attempts-heading">
        <h2 className={styles.cardTitle} id="payment-attempts-heading">
          Payment attempts
        </h2>
        <p className={styles.pageSubtitle}>
          Pending and failed attempts are shown separately. A failed attempt is not a posted payment
          and has no receipt.
        </p>

        {attempts.isPending && <PageSpinner label="Loading payment attempts" />}
        {attempts.isError && <Alert tone="error">{toDisplayMessage(attempts.error)}</Alert>}

        {attempts.isSuccess && attemptItems.length === 0 && (
          <p className={styles.empty}>No pending or failed payment attempts.</p>
        )}

        {attempts.isSuccess && attemptItems.length > 0 && (
          <div className={portalStyles.compactTableWrapper}>
            <table className={portalStyles.compactTable} aria-label="Payment attempts">
              <thead>
                <tr>
                  <th scope="col">Started</th>
                  <th scope="col">Reference</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Status</th>
                  <th scope="col">What to do</th>
                </tr>
              </thead>
              <tbody>
                {attemptItems.map((attempt) => (
                  <tr key={attempt.id}>
                    <td>{formatDate(attempt.createdAt)}</td>
                    <td>{attempt.internalReference}</td>
                    <td>{formatNgn(attempt.amount)}</td>
                    <td>
                      <StatusBadge status={attempt.status} />
                    </td>
                    <td>
                      {attempt.status === 'FAILED' ? (
                        <>
                          {attempt.failureMessage ??
                            'This attempt did not complete. Review the repayment before trying again.'}{' '}
                          <Link to={paths.customerRepayments}>Review repayment</Link>
                        </>
                      ) : attempt.status === 'SUCCEEDED' ? (
                        'Confirmed; the posted transaction appears above.'
                      ) : (
                        'No action is required while this attempt is processing.'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
